import { localTodayCalendarDate, parseCalendarDate } from '@shared/dates/calendarDate';
import React, { useState } from 'react';
import { Popover, Box, TextField, Button, Stack, Typography } from '@mui/material';
import { Patient } from '../../../../types/types';
import { MuiTelInput, matchIsValidTel } from 'mui-tel-input';
import { useSnackbar } from 'notistack';
import { useInvalidatePatientCache } from '@features/patients/hooks';
import { useAccessToken, useSubjectLanguage } from '@features/user';
import APIService from '../../../../services/APIService';
import SentryService from '../../../../services/SentryService';
import { capitalize } from '../../../../utils/utils';
import { getPatientPrimaryContactPhone } from '@utils/patientUtils';
import { z } from 'zod';

interface QuickPatientInfoPopoverProps {
  anchorPosition: { top: number; left: number } | null;
  open: boolean;
  onClose: () => void;
  patient: Patient;
  requiredFields: Array<'phone' | 'dob' | 'email'>;
  onSave: (updatedPatient: Partial<Patient>) => void;
}

// Validation schemas
const phoneSchema = z
  .string()
  .refine(
    val => matchIsValidTel(val, { onlyCountries: ['US', 'CA', 'GB', 'AU', 'NZ', 'ZA', 'MX'] }),
    { message: 'Please enter a valid phone number' },
  );

const emailSchema = z.string().email({ message: 'Please enter a valid email address' });

const dobSchema = z
  .string()
  .min(1, 'Date of birth is required')
  .refine(
    val => {
      const date = parseCalendarDate(val);
      return date !== null && date <= localTodayCalendarDate() && date > '1900-01-01';
    },
    { message: 'Please enter a valid date of birth' },
  );

export const QuickPatientInfoPopover: React.FC<QuickPatientInfoPopoverProps> = ({
  anchorPosition,
  open,
  onClose,
  patient,
  requiredFields,
  onSave,
}) => {
  const [phone, setPhone] = useState(getPatientPrimaryContactPhone(patient));
  const [email, setEmail] = useState(patient.email || '');
  const [dob, setDob] = useState(patient.dob || '');
  const [phoneError, setPhoneError] = useState<string | null>(null);
  const [emailError, setEmailError] = useState<string | null>(null);
  const [dobError, setDobError] = useState<string | null>(null);
  const [contactError, setContactError] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const { enqueueSnackbar } = useSnackbar();
  const { getAccessToken } = useAccessToken();
  const subjectLanguage = useSubjectLanguage();
  const invalidatePatientCache = useInvalidatePatientCache();

  const needsContact = requiredFields.includes('phone') && requiredFields.includes('email');

  const handlePhoneChange = (newValue: string) => {
    setPhone(newValue);
    if (phoneError) setPhoneError(null);
    if (contactError) setContactError(null);
  };

  const handleEmailChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setEmail(e.target.value);
    if (emailError) setEmailError(null);
    if (contactError) setContactError(null);
  };

  const handleDobChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setDob(e.target.value);
    if (dobError) setDobError(null);
  };

  const handleSave = async () => {
    setPhoneError(null);
    setEmailError(null);
    setDobError(null);
    setContactError(null);

    let hasErrors = false;

    if (needsContact) {
      const hasPhone = phone.trim().length > 0;
      const hasEmail = email.trim().length > 0;

      if (!hasPhone && !hasEmail) {
        setContactError('At least one contact method (email or phone) is required');
        hasErrors = true;
      }

      if (hasPhone) {
        const phoneResult = phoneSchema.safeParse(phone);
        if (!phoneResult.success) {
          setPhoneError(phoneResult.error.errors[0].message);
          hasErrors = true;
        }
      }

      if (hasEmail) {
        const emailResult = emailSchema.safeParse(email);
        if (!emailResult.success) {
          setEmailError(emailResult.error.errors[0].message);
          hasErrors = true;
        }
      }
    } else {
      if (requiredFields.includes('phone')) {
        const phoneResult = phoneSchema.safeParse(phone);
        if (!phoneResult.success) {
          setPhoneError(phoneResult.error.errors[0].message);
          hasErrors = true;
        }
      }

      if (requiredFields.includes('email')) {
        const emailResult = emailSchema.safeParse(email);
        if (!emailResult.success) {
          setEmailError(emailResult.error.errors[0].message);
          hasErrors = true;
        }
      }
    }

    if (requiredFields.includes('dob')) {
      const dobResult = dobSchema.safeParse(dob);
      if (!dobResult.success) {
        setDobError(dobResult.error.errors[0].message);
        hasErrors = true;
      }
    }

    if (hasErrors) {
      return;
    }

    setIsSubmitting(true);

    try {
      const updates: Partial<Patient> = {};

      if (needsContact) {
        if (phone.trim()) {
          updates.mobile_phone = phone;
          updates.phone = '';
        }
        if (email.trim()) updates.email = email;
      } else {
        if (requiredFields.includes('phone') && phone.trim()) {
          updates.mobile_phone = phone;
          updates.phone = '';
        }
        if (requiredFields.includes('email') && email.trim()) {
          updates.email = email;
        }
      }

      if (requiredFields.includes('dob') && dob) {
        updates.dob = dob;
      }

      // Update via API using the same pattern as PatientInformationView
      const accessToken = await getAccessToken();
      const response = await APIService.makeAPIPostRequest({
        requestString: '/patient/update',
        accessToken,
        body: {
          patient_id: patient.patient_id,
          updated_fields: updates,
        },
      });

      if (response.ok) {
        invalidatePatientCache(patient.patient_id);
        enqueueSnackbar(`${capitalize(subjectLanguage)} information updated`, {
          variant: 'success',
        });
        // Notify parent
        onSave(updates);
        onClose();
      } else {
        throw new Error(response.error?.message || `Failed to update ${subjectLanguage}`);
      }
    } catch (error) {
      console.error('Error updating patient:', error);
      enqueueSnackbar(
        error instanceof Error ? error.message : `Failed to update ${subjectLanguage}`,
        {
          variant: 'error',
        },
      );
      SentryService.logEvent('Error updating patient quick info', {
        level: 'error',
        tags: {
          patient_id: patient.patient_id,
        },
        extra: {
          error: error,
          requiredFields: requiredFields,
        },
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Popover
      open={open}
      anchorReference="anchorPosition"
      anchorPosition={anchorPosition || undefined}
      onClose={onClose}
      anchorOrigin={{
        vertical: 'top',
        horizontal: 'left',
      }}
      transformOrigin={{
        vertical: 'top',
        horizontal: 'left',
      }} slotProps={{ paper: {
        sx: { p: 2, minWidth: 300, maxWidth: 400 },
      } }}
    >
      <Stack spacing={2}>
        <Typography variant="subtitle1" sx={{ fontWeight: 'medium' }}>
          Add {capitalize(subjectLanguage)} Information
        </Typography>

        {needsContact && contactError && (
          <Typography variant="body2" color="error">
            {contactError}
          </Typography>
        )}

        {needsContact && (
          <Typography variant="body2" color="text.secondary">
            Provide at least one contact method
          </Typography>
        )}

        {requiredFields.includes('phone') && (
          <MuiTelInput
            label={needsContact ? 'Phone Number (optional)' : 'Phone Number'}
            value={phone}
            onChange={handlePhoneChange}
            defaultCountry="US"
            forceCallingCode
            onlyCountries={['US', 'CA', 'GB', 'AU', 'NZ', 'ZA', 'MX']}
            size="small"
            fullWidth
            autoFocus={true}
            error={!!phoneError}
            helperText={phoneError}
            sx={{
              '& input': {
                height: '1.4375em',
              },
            }}
          />
        )}

        {requiredFields.includes('email') && (
          <TextField
            label={needsContact ? 'Email (optional)' : 'Email'}
            name="email"
            type="email"
            value={email}
            onChange={handleEmailChange}
            variant="outlined"
            size="small"
            fullWidth
            autoFocus={!requiredFields.includes('phone')}
            error={!!emailError}
            helperText={emailError}
          />
        )}

        {requiredFields.includes('dob') && (
          <TextField
            label="Date of Birth"
            name="dob"
            type="date"
            value={dob}
            onChange={handleDobChange}
            variant="outlined"
            size="small"
            fullWidth
            required
            autoFocus={!requiredFields.includes('phone') && !requiredFields.includes('email')}
            error={!!dobError}
            helperText={dobError}
            slotProps={{
              inputLabel: { shrink: true },
              htmlInput: {
                max: localTodayCalendarDate(),
              },
            }}
          />
        )}

        <Stack direction="row" spacing={1} sx={{ justifyContent: 'flex-end' }}>
          <Button size="small" onClick={onClose} disabled={isSubmitting}>
            Cancel
          </Button>
          <Button size="small" variant="contained" onClick={handleSave} disabled={isSubmitting}>
            {isSubmitting ? 'Saving...' : 'Save'}
          </Button>
        </Stack>
      </Stack>
    </Popover>
  );
};
